import { useEditor, EditorContent, type JSONContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { TableKit } from '@tiptap/extension-table'
import { useEffect, useState } from 'react'
import { RawHtml } from './extensions/RawHtml'
import { BlogEditorToolbar } from './BlogEditorToolbar'
import './blog-editor.css'

export interface BlogEditorValue {
  /** TipTap ProseMirror document JSON (source of truth). */
  json: JSONContent | null
  /** Rendered HTML snapshot (sent to the API, sanitized server-side). */
  html: string
}

interface BlogEditorProps {
  value?: BlogEditorValue
  onChange?: (value: BlogEditorValue) => void
  placeholder?: string
}

const extensions = [
  // StarterKit v3 bundles link + underline; configure them here rather than
  // adding the extensions separately (which would duplicate them).
  StarterKit.configure({
    heading: { levels: [2, 3, 4] },
    link: { openOnClick: false, autolink: true },
  }),
  Image,
  TableKit.configure({ table: { resizable: true } }),
  RawHtml,
]

/**
 * Controlled rich-text editor. Behaves like any Ant Form field: it takes a
 * `value` ({ json, html }) and emits `onChange` on every edit. TipTap JSON is
 * the source of truth; the HTML snapshot is what the public site renders.
 */
export function BlogEditor({ value, onChange, placeholder }: BlogEditorProps) {
  const [focused, setFocused] = useState(false)

  const editor = useEditor({
    extensions: [
      ...extensions,
      Placeholder.configure({ placeholder: placeholder || 'Počnite da pišete…' }),
    ],
    content: value?.json ?? '',
    // Required under React 19 + StrictMode double-mount.
    immediatelyRender: false,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    onUpdate: ({ editor }) => {
      onChange?.({ json: editor.getJSON(), html: editor.getHTML() })
    },
  })

  // Sync external value into the editor (e.g. when an edit form loads data)
  // without clobbering in-progress typing.
  useEffect(() => {
    if (!editor) return
    const incoming = value?.json
    if (!incoming) return
    const current = editor.getJSON()
    if (JSON.stringify(current) !== JSON.stringify(incoming)) {
      editor.commands.setContent(incoming, { emitUpdate: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value?.json])

  return (
    <div className={`blog-editor${focused ? ' is-focused' : ''}`}>
      <BlogEditorToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  )
}

export default BlogEditor
